import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Card,
  CardBody,
  IconButton,
  Tooltip,
  Code,
  useToast,
  Skeleton,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { FiCheckCircle, FiXCircle, FiTrash2, FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import Links from '../components/Links';

// API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

function Dashboard({ 
  summaryData, 
  configData, 
  loading, 
  error, 
  showManagement, 
  setShowManagement, 
  fetchData, 
  refreshing, 
  setRefreshing 
}) {

  const toast = useToast();
  const { onClose } = useDisclosure();
  const [showERC20Fields, setShowERC20Fields] = useState(false);
  const [showSPLFields, setShowSPLFields] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editName, setEditName] = useState('');

  // Group addresses by blockchain
  const groupByBlockchain = (addresses) => {
    const groups = {};
    
    // Sort addresses by wallet name first
    const sortedAddresses = [...addresses].sort((a, b) => {
      const nameA = (a.walletName || '').toLowerCase();
      const nameB = (b.walletName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
  sortedAddresses.forEach(address => {
    const blockchain = (address.chain === 'bitcoin' || address.token === 'BTC') ? 'Bitcoin' :
                      (address.chain === 'dogecoin' || address.token === 'DOGE') ? 'Dogecoin' :
                      (address.chain === 'xrp' || address.token === 'XRP') ? 'XRP' :
                      address.tokenType === 'ERC20' ? 'EVM' : 
                      address.tokenType === 'ETH' ? 'EVM' : 
                      address.tokenType === 'SPL' ? 'Solana' :
                      address.tokenType === 'SOL' ? 'Solana' :
                      address.tokenType;
    
    if (!groups[blockchain]) {
      groups[blockchain] = [];
    }
    groups[blockchain].push(address);
  });
    
    return groups;
  };

  // Get monitoring status for an address
  const getMonitoringStatus = (address) => {
    if (!configData) return { pagerduty: false, slack: false };
    
    const pagerdutyAddresses = configData.pagerDutyConfig.map(addr => addr.address);
    const slackAddresses = configData.slackConfig.map(addr => addr.address);
    
    return {
      pagerduty: pagerdutyAddresses.includes(address),
      slack: slackAddresses.includes(address)
    };
  };

  // Get chain display for an address
  const getChainDisplay = (address) => {
    if (address.tokenType === 'SOL' || address.tokenType === 'SPL') {
      return { name: 'Solana', colorScheme: 'purple' };
    } else if (address.token === 'DOGE' || address.chain === 'dogecoin') {
      return { name: 'Dogecoin', colorScheme: 'yellow' };
    } else if (address.token === 'XRP' || address.chain === 'xrp') {
      return { name: 'XRP', colorScheme: 'blue' };
    } else if (address.tokenType === 'ETH' || address.tokenType === 'ERC20') {
      // Check if chain data exists in the address object
      if (address.chain && address.chain !== 'nochain') {
        // Map specific chains to appropriate colors
        const chainColors = {
          'ethereum': 'blue',
          'binance': 'yellow',
          'polygon': 'purple',
          'arbitrum': 'cyan',
          'optimism': 'red',
          'avalanche': 'orange',
          'bitcoin': 'orange',
          'dogecoin': 'yellow',
          'xrp': 'blue'
        };
        // For ETH token type, show the actual chain name
        const chainName = address.chain.charAt(0).toUpperCase() + address.chain.slice(1);
        return { 
          name: chainName, 
          colorScheme: chainColors[address.chain] || 'gray' 
        };
      } else {
        return { name: 'Ethereum', colorScheme: 'blue' };
      }
    }
    return { name: address.chain || 'Unknown', colorScheme: 'gray' };
  };

  // Get last balance change status with color coding - now uses API historical data
  const getLastBalanceChangeStatus = () => {
    if (!summaryData?.summary || summaryData.summary.length === 0) {
      return { color: 'gray.500', message: 'No data available', status: 'none' };
    }

    // Find the most recent balance change across all addresses using API data
    let mostRecentChange = null;
    let mostRecentTime = 0;

    summaryData.summary.forEach(address => {
      if (address.balanceHistory && address.balanceHistory.length > 1) {
        // Check all entries to find the most recent change within the last 12 hours
        const now = Date.now();
        const twelveHoursAgo = now - (12 * 60 * 60 * 1000);
        
        for (let i = address.balanceHistory.length - 1; i > 0; i--) {
          const currentEntry = address.balanceHistory[i];
          const previousEntry = address.balanceHistory[i - 1];
          
          if (currentEntry && previousEntry && currentEntry.balance !== previousEntry.balance) {
            const changeTime = new Date(currentEntry.date).getTime();
            
            // Only consider changes within the last 12 hours
            if (changeTime >= twelveHoursAgo && changeTime > mostRecentTime) {
              mostRecentTime = changeTime;
              mostRecentChange = {
                address: address.address,
                walletName: address.walletName,
                date: currentEntry.date,
                time: changeTime
              };
            }
          }
        }
      }
    });

    if (!mostRecentChange) {
      return { color: 'gray.500', message: 'No recent changes', status: 'none' };
    }

    const now = Date.now();
    const timeDiff = now - mostRecentTime;
    const hoursDiff = timeDiff / (1000 * 60 * 60);

    if (hoursDiff <= 1) {
      return { 
        color: 'red.500', 
        message: `Last change: ${Math.round(hoursDiff * 60)}m ago`,
        status: 'recent',
        details: mostRecentChange
      };
    } else if (hoursDiff <= 12) {
      return { 
        color: 'yellow.500', 
        message: `Last change: ${Math.round(hoursDiff)}h ago`,
        status: 'warning',
        details: mostRecentChange
      };
    } else {
      return { 
        color: 'green.500', 
        message: `Last change: ${Math.round(hoursDiff)}h ago`,
        status: 'stable',
        details: mostRecentChange
      };
    }
  };

  // Get per-wallet balance change status - now uses API historical data
  const getWalletChangeStatus = (address) => {
    if (!address.balanceHistory || address.balanceHistory.length < 2) {
      return { color: 'gray.500', message: 'No history', status: 'none', trend: 'stable' };
    }

    const now = Date.now();
    const twelveHoursAgo = now - (12 * 60 * 60 * 1000);
    
    // Calculate total balance change over the last 12 hours
    let totalChange = 0;
    let lastChangeTime = 0;
    let hasChangesInWindow = false;
    
    // Find the oldest entry within 12 hours to calculate total change
    let oldestEntryInWindow = null;
    let newestEntryInWindow = null;
    
    for (let i = 0; i < address.balanceHistory.length; i++) {
      const entry = address.balanceHistory[i];
      const entryTime = new Date(entry.date).getTime();
      
      if (entryTime >= twelveHoursAgo) {
        if (!oldestEntryInWindow) {
          oldestEntryInWindow = entry;
        }
        newestEntryInWindow = entry;
      }
    }
    
    // Calculate total change from oldest to newest within 12-hour window
    if (oldestEntryInWindow && newestEntryInWindow) {
      totalChange = newestEntryInWindow.balance - oldestEntryInWindow.balance;
      hasChangesInWindow = true;
    }
    
    // Find the time of the last balance change (any change, not just within 8 hours)
    for (let i = address.balanceHistory.length - 1; i > 0; i--) {
      const currentEntry = address.balanceHistory[i];
      const previousEntry = address.balanceHistory[i - 1];
      
      if (currentEntry && previousEntry && currentEntry.balance !== previousEntry.balance) {
        lastChangeTime = new Date(currentEntry.date).getTime();
        break;
      }
    }
    
    if (!hasChangesInWindow && lastChangeTime === 0) {
      return { color: 'gray.500', message: 'No recent changes', status: 'stable', trend: 'stable' };
    }

    const timeDiff = now - lastChangeTime;
    const hoursDiff = timeDiff / (1000 * 60 * 60);

    // Determine trend based on total change over 12 hours
    let trend = 'stable';
    if (totalChange > 0) trend = 'increasing';
    else if (totalChange < 0) trend = 'decreasing';

    // Determine urgency based on time since last change and trend
    if (hoursDiff <= 1) {
      if (trend === 'decreasing') {
        return { 
          color: 'red.500', 
          message: `${Math.round(hoursDiff * 60)}m ago`,
          status: 'critical',
          trend: 'decreasing',
          change: totalChange
        };
      } else if (trend === 'increasing') {
        return { 
          color: 'green.500', 
          message: `${Math.round(hoursDiff * 60)}m ago`,
          status: 'recent',
          trend: 'increasing',
          change: totalChange
        };
      }
    } else if (hoursDiff <= 12) {
      if (trend === 'decreasing') {
        return { 
          color: 'yellow.500', 
          message: `${Math.round(hoursDiff)}h ago`,
          status: 'warning',
          trend: 'decreasing',
          change: totalChange
        };
      } else if (trend === 'increasing') {
        return { 
          color: 'blue.500', 
          message: `${Math.round(hoursDiff)}h ago`,
          status: 'recent',
          trend: 'increasing',
          change: totalChange
        };
      }
    }

    return { 
      color: 'gray.500', 
      message: `${Math.round(hoursDiff)}h ago`,
      status: 'stable',
      trend: 'stable',
      change: totalChange
    };
  };

  // Format balance with appropriate decimals
  const formatBalance = (balance, token) => {
    if (balance === null || balance === undefined) return 'N/A';
    
    const decimals = token === 'WETH' || token === 'ETH' || token === 'BNB' || token === 'AVAX' || token === 'BTC' || token === 'DOGE' || token === 'XRP' ? 6 : 
                    token === 'SOL' ? 6 : 2;
    
    return parseFloat(balance).toFixed(decimals);
  };


  // Get explorer URL for address
  const getExplorerUrl = (address, tokenType, chain) => {
    if (tokenType === 'SOL' || tokenType === 'SPL') {
      return `https://solscan.io/account/${address}`;
    } else if (chain === 'dogecoin' || tokenType === 'DOGE') {
      return `https://dogechain.info/address/${address}`;
    } else if (chain === 'xrp' || tokenType === 'XRP') {
      return `https://xrpscan.com/account/${address}`;
    } else if (tokenType === 'ETH' || tokenType === 'ERC20') {
      // Use chain-specific explorer URLs
      if (chain === 'avalanche') {
        return `https://snowtrace.io/address/${address}`;
      } else if (chain === 'binance') {
        return `https://bscscan.com/address/${address}`;
      } else if (chain === 'bitcoin') {
        return `https://blockchain.com/explorer/addresses/btc/${address}`;
      } else if (chain === 'dogecoin') {
        return `https://dogechain.info/address/${address}`;
      } else if (chain === 'xrp') {
        return `https://xrpscan.com/account/${address}`;
      } else {
        return `https://etherscan.io/address/${address}`;
      }
    }
    return null;
  };

  // Manage address (add/remove/edit)
  const manageAddress = async (action, type, data) => {
    try {
      const response = await fetch(`${API_BASE_URL}/manage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action, type, data }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to manage address');
      }

      toast({
        title: 'Success',
        description: result.message,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Refresh data after successful operation
      await fetchData();
      onClose();

    } catch (err) {
      toast({
        title: 'Error',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Edit address name
  const editAddressName = async (address, newName) => {
    const monitoring = getMonitoringStatus(address);
    const monitoringType = monitoring.pagerduty ? 'pagerduty' : 'slack';
    
    await manageAddress('edit', monitoringType, {
      address: address,
      name: newName
    });
    
    // Reset edit state
    setEditingAddress(null);
    setEditName('');
  };

  // Start editing an address name
  const startEditing = (address, currentName) => {
    setEditingAddress(address);
    setEditName(currentName);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingAddress(null);
    setEditName('');
  };

  // Loading state
  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50">
        <Container maxW="full" px={3} py={6}>
          <VStack spacing={6} align="stretch">
            <Skeleton height="60px" />
            <Skeleton height="120px" />
            <Skeleton height="300px" />
          </VStack>
        </Container>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box minH="100vh" bg="gray.50">
        <Container maxW="full" px={3} py={6}>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="md">Error loading data!</AlertTitle>
              <AlertDescription fontSize="sm">{error}</AlertDescription>
            </Box>
            <Button ml={4} onClick={() => fetchData(true)} size="sm">
              Retry
            </Button>
          </Alert>
        </Container>
      </Box>
    );
  }

  if (!summaryData || !configData) {
    return (
      <Box minH="100vh" bg="gray.50">
        <Container maxW="full" px={3} py={6}>
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <AlertTitle fontSize="md">No data available</AlertTitle>
          </Alert>
        </Container>
      </Box>
    );
  }

  const blockchainGroups = groupByBlockchain(summaryData.summary);

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Main Content */}
      <Container maxW="full" px={6} py={6}>
        <VStack spacing={6} align="stretch">
          {/* Compact Stats */}
          <SimpleGrid columns={{ base: 1, sm: 2, md: 5 }} spacing={4}>
            <Box bg="white" p={4} borderRadius="md" border="1px" borderColor="gray.200">
              <Stat>
                <StatLabel fontSize="xs" color="gray.600" fontWeight="medium">
                  Total Addresses
                </StatLabel>
                <StatNumber fontSize="lg" color="gray.900" fontWeight="bold">
                  {summaryData.totalAddresses}
                </StatNumber>
              </Stat>
            </Box>
            
            <Box bg="white" p={4} borderRadius="md" border="1px" borderColor="gray.200">
              <Stat>
                <StatLabel fontSize="xs" color="gray.600" fontWeight="medium">
                  PagerDuty
                </StatLabel>
                <StatNumber fontSize="lg" color="red.500" fontWeight="bold">
                  {summaryData.pagerDutyAddresses}
                </StatNumber>
              </Stat>
            </Box>
            
            <Box bg="white" p={4} borderRadius="md" border="1px" borderColor="gray.200">
              <Stat>
                <StatLabel fontSize="xs" color="gray.600" fontWeight="medium">
                  Slack
                </StatLabel>
                <StatNumber fontSize="lg" color="blue.500" fontWeight="bold">
                  {summaryData.slackAddresses}
                </StatNumber>
              </Stat>
            </Box>
            
            <Box bg="white" p={4} borderRadius="md" border="1px" borderColor="gray.200">
              <Stat>
                <StatLabel fontSize="xs" color="gray.600" fontWeight="medium">
                  Last Change
                </StatLabel>
                <StatNumber fontSize="lg" color={getLastBalanceChangeStatus().color} fontWeight="bold">
                  {getLastBalanceChangeStatus().message}
                </StatNumber>
                {getLastBalanceChangeStatus().details && (
                  <StatHelpText fontSize="xs" color="gray.500" mt={1}>
                    {getLastBalanceChangeStatus().details.walletName}
                  </StatHelpText>
                )}
              </Stat>
            </Box>
            
            <Box bg="white" p={4} borderRadius="md" border="1px" borderColor="gray.200">
              <Stat>
                <StatLabel fontSize="xs" color="gray.600" fontWeight="medium">
                  Critical Alerts
                </StatLabel>
                <StatNumber fontSize="lg" color="red.500" fontWeight="bold">
                  {summaryData?.summary ? summaryData.summary.filter(addr => {
                    const status = getWalletChangeStatus(addr);
                    return status.status === 'critical';
                  }).length : 0}
                </StatNumber>
                <StatHelpText fontSize="xs" color="gray.500" mt={1}>
                  Decreasing ≤1h
                </StatHelpText>
              </Stat>
            </Box>
          </SimpleGrid>

          {/* Management Section */}
          {showManagement && (
            <Box>
              <Heading size="md" mb={4} color="gray.800" fontWeight="semibold">
                Address Management
              </Heading>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {/* Add Address Form */}
                <Card bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                  <CardBody p={6}>
                    <VStack spacing={4} align="stretch">
                      <Heading size="md" color="gray.900" fontWeight="bold">Add New Address</Heading>
                      <FormControl>
                        <FormLabel fontSize="sm" color="gray.700" fontWeight="medium">Blockchain</FormLabel>
                        <Select
                          placeholder="Select blockchain"
                          size="sm"
                          id="blockchain"
                          bg="white"
                          borderColor="gray.300"
                          color="gray.900"
                          _focus={{ borderColor: "brand.500" }}
                          _placeholder={{ color: "gray.500" }}
                        >
                          <option value="solana">Solana</option>
                          <option value="ethereum">Ethereum</option>
                          <option value="binance">Binance</option>
                          <option value="avalanche">Avalanche</option>
                          <option value="bitcoin">Bitcoin</option>
                          <option value="dogecoin">Dogecoin</option>
                          <option value="xrp">XRP</option>
                          {/* Add more options as needed */}
                        </Select>
                      </FormControl>
                      
                      <FormControl>
                        <FormLabel fontSize="sm" color="gray.700" fontWeight="medium">Address</FormLabel>
                        <Input
                          placeholder="Enter blockchain address"
                          size="sm"
                          id="address"
                          bg="white"
                          borderColor="gray.300"
                          color="gray.900"
                          _focus={{ borderColor: "brand.500" }}
                          _placeholder={{ color: "gray.500" }}
                        />
                      </FormControl>
                      
                      <FormControl>
                        <FormLabel fontSize="sm" color="gray.700" fontWeight="medium">Name</FormLabel>
                        <Input
                          placeholder="Wallet name (optional)"
                          size="sm"
                          id="name"
                          bg="white"
                          borderColor="gray.300"
                          color="gray.900"
                          _focus={{ borderColor: "brand.500" }}
                          _placeholder={{ color: "gray.500" }}
                        />
                      </FormControl>
                      
                      <FormControl>
                        <FormLabel fontSize="sm" color="gray.700" fontWeight="medium">Token Type</FormLabel>
                        <Select 
                          size="sm" 
                          id="token" 
                          bg="white" 
                          borderColor="gray.300" 
                          color="gray.900"
                          onChange={(e) => {
                            setShowERC20Fields(e.target.value === 'ERC20');
                            setShowSPLFields(e.target.value === 'SPL');
                          }}
                        >
                          <option value="SOL">SOL (Solana)</option>
                          <option value="SPL">SPL Token (Solana)</option>
                          <option value="ETH">Native EVM (ETH/BNB/...)</option>
                          <option value="ERC20">ERC20 Token</option>
                        </Select>
                      </FormControl>
                      
                      {showERC20Fields && (
                        <>
                          <FormControl>
                            <FormLabel fontSize="sm" color="gray.700" fontWeight="medium">Contract Address</FormLabel>
                            <Input
                              placeholder="Enter ERC20 contract address"
                              size="sm"
                              id="contractAddress"
                              bg="white"
                              borderColor="gray.300"
                              color="gray.900"
                              _focus={{ borderColor: "brand.500" }}
                              _placeholder={{ color: "gray.500" }}
                            />
                          </FormControl>
                          
                          <FormControl>
                            <FormLabel fontSize="sm" color="gray.700" fontWeight="medium">Token Symbol</FormLabel>
                            <Input
                              placeholder="e.g., WETH, USDC, USDT"
                              size="sm"
                              id="tokenSymbol"
                              bg="white"
                              borderColor="gray.300"
                              color="gray.900"
                              _focus={{ borderColor: "brand.500" }}
                              _placeholder={{ color: "gray.500" }}
                            />
                          </FormControl>
                          
                          <FormControl>
                            <FormLabel fontSize="sm" color="gray.700" fontWeight="medium">Decimals</FormLabel>
                            <Input
                              placeholder="e.g., 18, 6, 8"
                              size="sm"
                              id="tokenDecimals"
                              bg="white"
                              borderColor="gray.300"
                              color="gray.900"
                              _focus={{ borderColor: "brand.500" }}
                              _placeholder={{ color: "gray.500" }}
                              type="number"
                            />
                          </FormControl>
                        </>
                      )}
                      
                      {showSPLFields && (
                        <>
                          <FormControl>
                            <FormLabel fontSize="sm" color="gray.700" fontWeight="medium">Mint Address</FormLabel>
                            <Input
                              placeholder="Enter SPL token mint address"
                              size="sm"
                              id="mintAddress"
                              bg="white"
                              borderColor="gray.300"
                              color="gray.900"
                              _focus={{ borderColor: "brand.500" }}
                              _placeholder={{ color: "gray.500" }}
                            />
                          </FormControl>
                          
                          <FormControl>
                            <FormLabel fontSize="sm" color="gray.700" fontWeight="medium">Token Symbol</FormLabel>
                            <Input
                              placeholder="e.g., USDC, USDT, RAY"
                              size="sm"
                              id="splTokenSymbol"
                              bg="white"
                              borderColor="gray.300"
                              color="gray.900"
                              _focus={{ borderColor: "brand.500" }}
                              _placeholder={{ color: "gray.500" }}
                            />
                          </FormControl>
                          
                          <FormControl>
                            <FormLabel fontSize="sm" color="gray.700" fontWeight="medium">Decimals</FormLabel>
                            <Input
                              placeholder="e.g., 9, 6, 8"
                              size="sm"
                              id="splTokenDecimals"
                              bg="white"
                              borderColor="gray.300"
                              color="gray.900"
                              _focus={{ borderColor: "brand.500" }}
                              _placeholder={{ color: "gray.500" }}
                              type="number"
                            />
                          </FormControl>
                        </>
                      )}
                      
                      <FormControl>
                        <FormLabel fontSize="sm" color="gray.700" fontWeight="medium">Monitoring Type</FormLabel>
                        <Select size="sm" id="monitoringType" bg="white" borderColor="gray.300" color="gray.900">
                          <option value="slack">Slack Notifications</option>
                          <option value="pagerduty">PagerDuty Alerts</option>
                        </Select>
                      </FormControl>
                      
                      <Button
                        colorScheme="green"
                        size="sm"
                        onClick={() => {
                          const address = document.getElementById('address').value;
                          const name = document.getElementById('name').value;
                          const chain = document.getElementById('blockchain').value; // Get the selected chain
                          const token = document.getElementById('token').value;
                          const monitoringType = document.getElementById('monitoringType').value;
                          const contractAddress = document.getElementById('contractAddress')?.value || '';
                          const tokenSymbol = document.getElementById('tokenSymbol')?.value || '';
                          const tokenDecimals = document.getElementById('tokenDecimals')?.value || '';
                          const mintAddress = document.getElementById('mintAddress')?.value || '';
                          const splTokenSymbol = document.getElementById('splTokenSymbol')?.value || '';
                          const splTokenDecimals = document.getElementById('splTokenDecimals')?.value || '';
                          
                          if (!address) {
                            toast({
                              title: 'Error',
                              description: 'Address is required',
                              status: 'error',
                              duration: 3000,
                              isClosable: true,
                            });
                            return;
                          }
                          
                          if (token === 'ERC20') {
                            if (!contractAddress) {
                              toast({
                                title: 'Error',
                                description: 'Contract address is required for ERC20 tokens',
                                status: 'error',
                                duration: 3000,
                                isClosable: true,
                              });
                              return;
                            }
                            if (!tokenSymbol) {
                              toast({
                                title: 'Error',
                                description: 'Token symbol is required for ERC20 tokens',
                                status: 'error',
                                duration: 3000,
                                isClosable: true,
                              });
                              return;
                            }
                            if (!tokenDecimals) {
                              toast({
                                title: 'Error',
                                description: 'Decimals is required for ERC20 tokens',
                                status: 'error',
                                duration: 3000,
                                isClosable: true,
                              });
                              return;
                            }
                          }
                          
                          if (token === 'SPL') {
                            if (!mintAddress) {
                              toast({
                                title: 'Error',
                                description: 'Mint address is required for SPL tokens',
                                status: 'error',
                                duration: 3000,
                                isClosable: true,
                              });
                              return;
                            }
                            if (!splTokenSymbol) {
                              toast({
                                title: 'Error',
                                description: 'Token symbol is required for SPL tokens',
                                status: 'error',
                                duration: 3000,
                                isClosable: true,
                              });
                              return;
                            }
                            if (!splTokenDecimals) {
                              toast({
                                title: 'Error',
                                description: 'Decimals is required for SPL tokens',
                                status: 'error',
                                duration: 3000,
                                isClosable: true,
                              });
                              return;
                            }
                          }
                          
                          const data = {
                            address,
                            name: name || address,
                            token,
                            chain,
                          };
                          
                          if (token === 'ERC20') {
                            data.contractAddress = contractAddress;
                            data.symbol = tokenSymbol;
                            data.decimals = parseInt(tokenDecimals);
                          }
                          
                          if (token === 'SPL') {
                            data.mintAddress = mintAddress;
                            data.symbol = splTokenSymbol;
                            data.decimals = parseInt(splTokenDecimals);
                          }
                          
                          manageAddress('add', monitoringType, data);
                        }}
                      >
                        Add Address
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>
                
                {/* Current Addresses */}
                <Card bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                  <CardBody p={6}>
                    <VStack spacing={4} align="stretch">
                      <Heading size="md" color="gray.900" fontWeight="bold">Current Addresses</Heading>
                      
                      {summaryData && summaryData.summary && (
                        <VStack spacing={3} align="stretch" maxH="300px" overflowY="auto">
                          {[...summaryData.summary].sort((a, b) => {
                            const nameA = (a.walletName || '').toLowerCase();
                            const nameB = (b.walletName || '').toLowerCase();
                            return nameA.localeCompare(nameB);
                          }).map((address, index) => {
                            const monitoring = getMonitoringStatus(address.address);
                            return (
                              <Box key={index} p={4} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.200">
                                <HStack justify="space-between" align="start">
                                  <VStack align="start" spacing={2}>
                                    {editingAddress === address.address ? (
                                      <InputGroup size="sm">
                                        <Input
                                          value={editName}
                                          onChange={(e) => setEditName(e.target.value)}
                                          placeholder="Enter wallet name"
                                          bg="white"
                                          borderColor="gray.300"
                                          color="gray.900"
                                          _focus={{ borderColor: "brand.500" }}
                                        />
                                        <InputRightElement width="auto" pr={2}>
                                          <HStack spacing={0.5} align="center">
                                            <IconButton
                                              icon={<FiCheck />}
                                              size="xs"
                                              colorScheme="green"
                                              variant="ghost"
                                              onClick={() => editAddressName(address.address, editName)}
                                              minW="auto"
                                              w="20px"
                                              h="20px"
                                            />
                                            <IconButton
                                              icon={<FiX />}
                                              size="xs"
                                              colorScheme="red"
                                              variant="ghost"
                                              onClick={cancelEditing}
                                              minW="auto"
                                              w="20px"
                                              h="20px"
                                            />
                                          </HStack>
                                        </InputRightElement>
                                      </InputGroup>
                                    ) : (
                                      <HStack spacing={2}>
                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                          {address.walletName}
                                        </Text>
                                        <IconButton
                                          icon={<FiEdit2 />}
                                          size="xs"
                                          colorScheme="blue"
                                          variant="ghost"
                                          onClick={() => startEditing(address.address, address.walletName)}
                                        />
                                      </HStack>
                                    )}
                                    <Code fontSize="xs" colorScheme="gray" bg="white" color="gray.700" p={2} borderRadius="sm">
                                      {address.address.slice(0, 12)}...{address.address.slice(-12)}
                                    </Code>
                                    <HStack spacing={2}>
                                      {monitoring.pagerduty && (
                                        <Badge colorScheme="red" size="sm" fontSize="xs" fontWeight="semibold">PD</Badge>
                                      )}
                                      {monitoring.slack && (
                                        <Badge colorScheme="blue" size="sm" fontSize="xs" fontWeight="semibold">SL</Badge>
                                      )}
                                  {(() => {
                                    const walletStatus = getWalletChangeStatus(address);
                                    if (walletStatus.status === 'critical') {
                                      return <Badge colorScheme="red" size="sm" fontSize="xs" fontWeight="semibold">↓</Badge>;
                                    } else if (walletStatus.status === 'warning') {
                                      return <Badge colorScheme="yellow" size="sm" fontSize="xs" fontWeight="semibold">⚠</Badge>;
                                    } else if (walletStatus.status === 'recent' && walletStatus.trend === 'increasing') {
                                      return <Badge colorScheme="green" size="sm" fontSize="xs" fontWeight="semibold">↑</Badge>;
                                    } else if (walletStatus.status === 'recent' && walletStatus.trend === 'decreasing') {
                                      return <Badge colorScheme="red" size="sm" fontSize="xs" fontWeight="semibold">↓</Badge>;
                                    }
                                    return null;
                                  })()}
                                    </HStack>
                                  </VStack>
                                  <IconButton
                                    icon={<FiTrash2 />}
                                    size="sm"
                                    colorScheme="red"
                                    variant="outline"
                                    onClick={() => {
                                      const monitoringType = monitoring.pagerduty ? 'pagerduty' : 'slack';
                                      manageAddress('remove', monitoringType, {
                                        address: address.address
                                      });
                                    }}
                                  />
                                </HStack>
                              </Box>
                            );
                          })}
                        </VStack>
                      )}
                    </VStack>
                  </CardBody>
                </Card>
              </SimpleGrid>
            </Box>
          )}

          {/* Compact Tables */}
          {Object.entries(blockchainGroups).map(([blockchain, addresses]) => (
            <Box key={blockchain}>
              <HStack spacing={2} mb={3}>
                <Heading size="md" color="gray.800" fontWeight="semibold">
                  {blockchain}
                </Heading>
                <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                  {addresses.length}
                </Badge>
              </HStack>
              
              <Box bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                <TableContainer>
                  <Table variant="simple" size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          Wallet
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          Address
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3} isNumeric>
                          Balance
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          Chain
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          Status
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          Actions
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {addresses.map((address, index) => {
                        const monitoring = getMonitoringStatus(address.address);
                        const chainInfo = getChainDisplay(address);
                        return (
                          <Tr key={index} _hover={{ bg: 'gray.50' }}>
                            <Td py={3}>
                              {editingAddress === address.address ? (
                                <InputGroup size="sm">
                                  <Input
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    placeholder="Enter wallet name"
                                    bg="white"
                                    borderColor="gray.300"
                                    color="gray.900"
                                    _focus={{ borderColor: "brand.500" }}
                                    fontSize="sm"
                                  />
                                  <InputRightElement width="auto" pr={2}>
                                    <HStack spacing={0.5} align="center">
                                      <IconButton
                                        icon={<FiCheck />}
                                        size="xs"
                                        colorScheme="green"
                                        variant="ghost"
                                        onClick={() => editAddressName(address.address, editName)}
                                        minW="auto"
                                        w="20px"
                                        h="20px"
                                      />
                                      <IconButton
                                        icon={<FiX />}
                                        size="xs"
                                        colorScheme="red"
                                        variant="ghost"
                                        onClick={cancelEditing}
                                        minW="auto"
                                        w="20px"
                                        h="20px"
                                      />
                                    </HStack>
                                  </InputRightElement>
                                </InputGroup>
                              ) : (
                                <HStack spacing={2}>
                                  <Text fontWeight="medium" color="gray.900" fontSize="sm">
                                    {address.walletName}
                                  </Text>
                                  <IconButton
                                    icon={<FiEdit2 />}
                                    size="xs"
                                    colorScheme="blue"
                                    variant="ghost"
                                    onClick={() => startEditing(address.address, address.walletName)}
                                  />
                                </HStack>
                              )}
                            </Td>
                            <Td py={3}>
                              <Tooltip label="Click to view on explorer" placement="top">
                                <Code 
                                  fontSize="xs" 
                                  colorScheme="gray" 
                                  p={2} 
                                  borderRadius="sm" 
                                  cursor="pointer"
                                  color="gray.800"
                                  bg="gray.100"
                                  _hover={{ bg: "gray.200" }}
                                  onClick={() => {
                                    const explorerUrl = getExplorerUrl(address.address, address.tokenType, address.chain);
                                    if (explorerUrl) {
                                      window.open(explorerUrl, '_blank');
                                    }
                                  }}
                                >
                                  {address.address}
                                </Code>
                              </Tooltip>
                            </Td>
                            <Td isNumeric py={3}>
                              {address.error ? (
                                <Text color="red.500" fontWeight="medium" fontSize="sm">Error</Text>
                              ) : (
                                <VStack spacing={0} align="end">
                                  <Text fontWeight="bold" color="gray.900" fontSize="sm">
                                    {formatBalance(address.currentBalance, address.token)}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {address.token}
                                  </Text>
                                </VStack>
                              )}
                            </Td>
                            <Td py={3}>
                              <Badge colorScheme={chainInfo.colorScheme} variant="subtle" fontSize="xs">
                                {chainInfo.name}
                              </Badge>
                            </Td>
                            <Td py={3}>
                              {address.error ? (
                                <HStack spacing={1}>
                                  <FiXCircle color="red" size={12} />
                                  <Text color="red.500" fontSize="xs">Error</Text>
                                </HStack>
                              ) : (
                                <HStack spacing={2}>
                                  <HStack spacing={1}>
                                    <FiCheckCircle color="green" size={12} />
                                    <Text color="green.500" fontSize="xs">OK</Text>
                                  </HStack>
                                  {(() => {
                                    const walletStatus = getWalletChangeStatus(address);
                                    if (walletStatus.status === 'critical') {
                                      return (
                                        <HStack spacing={1}>
                                          <Badge colorScheme="red" size="sm" fontSize="xs">↓</Badge>
                                          <Text color="red.500" fontSize="xs">{walletStatus.message}</Text>
                                        </HStack>
                                      );
                                    } else if (walletStatus.status === 'warning') {
                                      return (
                                        <HStack spacing={1}>
                                          <Badge colorScheme="yellow" size="sm" fontSize="xs">⚠</Badge>
                                          <Text color="yellow.600" fontSize="xs">{walletStatus.message}</Text>
                                        </HStack>
                                      );
                                    } else if (walletStatus.status === 'recent' && walletStatus.trend === 'increasing') {
                                      return (
                                        <HStack spacing={1}>
                                          <Badge colorScheme="green" size="sm" fontSize="xs">↑</Badge>
                                          <Text color="green.500" fontSize="xs">{walletStatus.message}</Text>
                                        </HStack>
                                      );
                                    } else if (walletStatus.status === 'recent' && walletStatus.trend === 'decreasing') {
                                      return (
                                        <HStack spacing={1}>
                                          <Badge colorScheme="red" size="sm" fontSize="xs">↓</Badge>
                                          <Text color="red.500" fontSize="xs">{walletStatus.message}</Text>
                                        </HStack>
                                      );
                                    }
                                    return null;
                                  })()}
                                </HStack>
                              )}
                            </Td>
                            <Td py={3}>
                              <Tooltip label="Delete address" placement="left">
                                <IconButton
                                  icon={<FiTrash2 />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="outline"
                                  onClick={() => {
                                    const monitoringType = monitoring.pagerduty ? 'pagerduty' : 'slack';
                                    manageAddress('remove', monitoringType, {
                                      address: address.address
                                    });
                                  }}
                                />
                              </Tooltip>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          ))}

          {/* Useful Links Section */}
          <Links />
        </VStack>
      </Container>
    </Box>
  );
}

export default Dashboard;
